import {
    ColumnDef,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    RowData,
    useReactTable,
} from '@tanstack/react-table';
import { Calendar, ChevronDown, ChevronUp, DollarSign, Download, Filter, Minus, Plus, Trash2, X } from 'lucide-react';
import Moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { v4 as uuidv4 } from 'uuid'; // Install dulu: npm install uuid
import { apiFetch } from '../lib/apiFetch';
export type AbsensiData = {
    id: number
    name: string
    checkin_date: string
    checkin_time: string
    checkout_date: string
    checkout_time: string
    lembur_hour: string
}
declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        filterVariant?: 'text' | 'range' | 'select'
    }
}
const AttendanceSystem = () => {
    const baseurl = import.meta.env.VITE_KOPILIBRASI_URL
    const [dataabsen, setDataabsen] = useState<AbsensiData[]>([])
    const dataabsenref = useRef<AbsensiData[]>([])
    // new: loading state and fetch control
    // const [loading, setLoading] = useState<boolean>(false)
    const fetchControllerRef = useRef<AbortController | null>(null)
    const isFirstDebounceRun = useRef<boolean>(true)
    Moment.locale('id');
    const [basicSalary, setBasicSalary] = useState<number>(700000)
    const [showModalGaji, setShowModalGaji] = useState<boolean>(false)
    const [employees, setEmployees] = useState<string[]>([])
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState<string>("")
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const defaultLembur = 10000
    const initStatefield = [
        { label: 'Gaji Pokok', content: 0, type: "debit" },
        { label: 'Lembur', content: defaultLembur, type: "debit" }
    ]
    const [incomeComponents, setIncomeComponents] = useState<{ id: string, name: string, amount: number }[]>([]);
    const [deductionComponents, setDeductionComponents] = useState<{ id: string, name: string, amount: number }[]>([]);
    const totalDaysPerMonth = 14
    const inputFieldsRef = useRef<any[]>(initStatefield)
    const columnHelper = createColumnHelper<AbsensiData>();
    const [overtime, setOvertime] = useState<number>(defaultLembur)

    const columns: ColumnDef<AbsensiData, any>[] = useMemo(() => [
        columnHelper.accessor('name', {
            header: 'Name',
            cell: info => (
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                        {(info.getValue() as string).charAt(0)}
                    </div>
                    <span className="font-semibold text-gray-900">{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('checkin_date', {
            header: 'Date',
            cell: info => (
                <div className="text-gray-700">
                    {new Date(info.getValue()).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })}
                </div>
            ),
        }),
        columnHelper.accessor('checkin_time', {
            header: 'Check-in Time',
            cell: info => (
                <div className="text-gray-700">
                    {info.getValue() ? (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                            {info.getValue()}
                        </span>
                    ) : (
                        <span className="text-gray-400">-</span>
                    )}
                </div>
            ),
        }),
        columnHelper.accessor('checkout_time', {
            header: 'Check-out Time',
            cell: info => (
                <div className="text-gray-700">
                    {info.getValue() ? (
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                            {info.getValue()}
                        </span>
                    ) : (
                        <span className="text-gray-400">-</span>
                    )}
                </div>
            ),
        }),
        columnHelper.accessor('lembur_hour', {
            header: 'Overtime',
            cell: info => (
                <div className="text-gray-700">
                    {info.getValue() ? (
                        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm font-bold">
                            {info.getValue()}
                        </span>
                    ) : (
                        <span className="text-gray-400">-</span>
                    )}
                </div>
            ),
        }),
        columnHelper.accessor('checkout_date', {
            header: 'Status',
            cell: info => {
                const isPresent = !!(info.row.original.checkout_time && info.row.original.checkout_time !== '');
                return (
                    <div className="text-gray-700">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isPresent
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {isPresent ? 'Done' : 'On Duty'}
                        </span>
                    </div>
                )
            },
        }),
    ], []);

    const filteredData: AbsensiData[] = useMemo(() => {
        return dataabsen.filter(item => {
            const matchesEmployee = !selectedEmployee || item.name.toLowerCase().includes(selectedEmployee.toLowerCase());
            const matchesDateRange = (!startDate || item.checkin_date >= startDate) && (!endDate || item.checkin_date <= endDate);
            return matchesEmployee && matchesDateRange;
        });
    }, [dataabsen, selectedEmployee, startDate, endDate]);

    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };
    const stats = useMemo(() => {
        const totalRecords = filteredData.length;
        const uniqueEmployees = new Set(filteredData.map(item => item.name)).size;
        const presentCount = filteredData.filter(item => (item.checkout_time === '' || item.checkout_time === null)).length;

        const attendanceRate = totalRecords > 0 ? (100 - Math.round((presentCount / totalRecords) * 100)) : 100;
        const avgOvertime = filteredData
            .filter(item => item.lembur_hour)
            .reduce((acc, item) => {
                const [hours, minutes] = item.lembur_hour.split(':').map(Number);
                return acc + hours + minutes / 60;
            }, 0) / filteredData.filter(item => item.lembur_hour).length || 0;

        return {
            totalRecords,
            uniqueEmployees,
            attendanceRate,
            avgOvertime: (avgOvertime * 60).toFixed(2), // Convert to minutes
        };
    }, [filteredData]);
    // updated: accept optional AbortSignal and set loading
    const fetchData = async (name?: string, datefrom?: string, dateto?: string, signal?: AbortSignal) => {
        // setLoading(true)
        try {
            const url = baseurl + `/list_absensi?employee_name=${name}&checkin_date_from=${datefrom}&checkin_date_to=${dateto}`
            const raw = await apiFetch(url, {
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                },
                signal,
            })
            if (!raw.ok) {
                // keep previous data if error; could show toast later
                withReactContent(Swal).fire({
                    title: "Error",
                    text: "Gagal mengambil data absensi",
                    icon: "error",
                    timer: 2000
                });
                return
            }
            const response = await raw.json()
            if (response.data && response.data.length > 0) {
                setDataabsen(response.data)
                dataabsenref.current = response.data
                if (employees.length <= 0) {
                    let tmp: string[] = []
                    response.data.forEach((item: any) => {
                        if (!tmp.includes(item.name)) tmp.push(item.name)
                    })
                    tmp = tmp.sort((a, b) => a.localeCompare(b))
                    tmp = ["", ...tmp]
                    setEmployees(tmp)
                }
            } else {
                setDataabsen([])
                dataabsenref.current = []
            }
        } catch (err: any) {
            if (err.name === 'AbortError') {
                // request was cancelled
                return
            }
            console.error('fetchData error', err)
        } finally {
            // setLoading(false)
        }
    }
    const adjustBasicSalary = (increment: number) => {
        setBasicSalary(prev => Math.max(0, prev + increment));
    };
    const adjustOvertime = (increment: number) => {
        setOvertime(prev => Math.max(0, prev + increment));
    }
    const resetModal = () => {
        inputFieldsRef.current = initStatefield;
        setIncomeComponents([]);
        setDeductionComponents([]);
    };
    const showError = (message: string) => {
        withReactContent(Swal).fire({
            title: "Error",
            text: message,
            icon: "error",
            timer: 2000
        });
    }
    const handleGenerateGaji = () => {
        let isError: boolean = false
        const errors = [
            ...incomeComponents.filter(item => item.name.trim() === '' || item.amount <= 0),
            ...deductionComponents.filter(item => item.name.trim() === '' || item.amount < 0),
        ];

        if (errors.length > 0) {
            showError("Lengkapi semua nama & jumlah komponen gaji");
            return
        }
        if (selectedEmployee === "") {
            showError("Pilih nama karyawan terlebih dahulu");
            return
        }
        const dataEmployee = {
            name: selectedEmployee,
            dateFrom: startDate,
            dateTo: endDate
        }
        const tmpgajian = inputFieldsRef.current.slice()

        if (tmpgajian.length > 2) {
            for (let i = 2; i < tmpgajian.length; i++) {
                if (tmpgajian[i].content === "" || tmpgajian[i].label === "") {
                    let msg: string = "Lengkapi kolom pengurangan"
                    if (tmpgajian[i].type == "debit") {
                        msg = "Lengkapi kolom pendapatan"
                    }
                    showError(msg);
                    isError = true
                    break;
                }
            }
        }
        if (isError) {
            return;
        }
        let lembur: number = 0
        for (let i = 0; i < filteredData.length; i++) {
            if (filteredData[i].lembur_hour && filteredData[i].lembur_hour !== "") {
                const splitlembur = filteredData[i].lembur_hour.split(":")
                const jam: number = Number(splitlembur[0])
                const menit: number = Number(splitlembur[1])
                lembur += jam
                if (menit >= 30) {
                    lembur++
                }
            }

        }
        tmpgajian[0].label = `${tmpgajian[0].label} (${filteredData.length} Hari)`
        tmpgajian[0].content = Number(((Number(basicSalary) / totalDaysPerMonth) * filteredData.length)).toFixed(0) || 0
        tmpgajian[1].label = `${tmpgajian[1].label} (${lembur} Jam)`
        tmpgajian[1].content = Number((lembur * Number(overtime))).toFixed(0) || 0
        tmpgajian.push(...incomeComponents.map(item => ({
            label: item.name,
            content: item.amount,
            type: "debit"
        })));
        tmpgajian.push(...deductionComponents.map(item => ({
            label: item.name,
            content: item.amount,
            type: "credit"
        })));
        localStorage.setItem("data-absen", JSON.stringify(filteredData))
        localStorage.setItem("data-gajian", JSON.stringify(tmpgajian))
        localStorage.setItem("data-employee", JSON.stringify(dataEmployee))
        setShowModalGaji(false)
        resetModal()
        window.open(`${window.location.origin}/pdfdownload`)
    }
    const addIncomeComponent = () => {
        setIncomeComponents(prev => [
            ...prev,
            { id: uuidv4(), name: '', amount: 0 }
        ]);
    };

    const updateIncomeComponent = (id: string, field: 'name' | 'amount', value: string | number) => {
        setIncomeComponents(prev =>
            prev.map(comp =>
                comp.id === id
                    ? { ...comp, [field]: field === 'amount' ? Number(value) : value }
                    : comp
            )
        );
    };

    const removeIncomeComponent = (id: string) => {
        setIncomeComponents(prev => prev.filter(comp => comp.id !== id));
    };
    const addDeductionComponent = () => {
        setDeductionComponents(prev => [
            ...prev,
            { id: uuidv4(), name: '', amount: 0 }
        ]);
    };

    const updateDeductionComponent = (id: string, field: 'name' | 'amount', value: string | number) => {
        setDeductionComponents(prev =>
            prev.map(comp =>
                comp.id === id
                    ? { ...comp, [field]: field === 'amount' ? Number(value) : value }
                    : comp
            )
        );
    };

    const removeDeductionComponent = (id: string) => {
        setDeductionComponents(prev => prev.filter(comp => comp.id !== id));
    };
    // initial fetch on mount (no debounce)
    useEffect(() => {
        const controller = new AbortController()
        fetchData("", "", "", controller.signal)
        // keep existing behavior (clearing storage)
        localStorage.clear()
        return () => controller.abort()
    }, [])

    // debounce fetch when filters change
    useEffect(() => {
        // skip first run because mount already fetched
        if (isFirstDebounceRun.current) {
            isFirstDebounceRun.current = false
            return
        }

        // abort previous pending fetch
        if (fetchControllerRef.current) {
            fetchControllerRef.current.abort()
        }
        const controller = new AbortController()
        fetchControllerRef.current = controller

        const handler = setTimeout(() => {
            fetchData(selectedEmployee, startDate, endDate, controller.signal)
        }, 500) // 500ms debounce

        return () => {
            clearTimeout(handler)
            controller.abort()
        }
    }, [selectedEmployee, startDate, endDate])

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
            <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-xl mb-6 md:mb-8 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-12 text-white">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">Employee Attendance System</h1>
                                <p className="text-blue-100 text-sm sm:text-base md:text-lg">Track and manage employee check-in/check-out times</p>
                            </div>
                            <div className="text-left sm:text-right">
                                <div className="text-base sm:text-xl md:text-2xl font-bold">
                                    {new Date().toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </div>
                                <div className="text-blue-100 text-sm sm:text-base">
                                    {new Date().toLocaleTimeString()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 md:mb-8">
                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Total Records</p>
                                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{stats.totalRecords}</p>
                            </div>
                            <div className="p-2 sm:p-3 bg-blue-100 rounded-full">
                                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-green-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Active Employees</p>
                                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{stats.uniqueEmployees}</p>
                            </div>
                            <div className="p-2 sm:p-3 bg-green-100 rounded-full">
                                <Filter className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-purple-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Attendance Rate</p>
                                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{stats.attendanceRate}%</p>
                            </div>
                            <div className="p-2 sm:p-3 bg-purple-100 rounded-full">
                                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-600 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs font-bold">%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-orange-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Avg. Overtime</p>
                                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{stats.avgOvertime} m</p>
                            </div>
                            <div className="p-2 sm:p-3 bg-orange-100 rounded-full">
                                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-600 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs font-bold">+</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 md:mb-8">
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                        <div className="col-span-2 sm:col-span-1">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Employee</label>
                            <select
                                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                value={selectedEmployee}
                                onChange={(e) => setSelectedEmployee(e.target.value)}
                            >
                                {
                                    employees && employees.map((item, index) => {
                                        return (
                                            <option key={index} value={item}>{item || 'All'}</option>
                                        )
                                    })
                                }
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
                            <input
                                type="date"
                                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
                            <input
                                type="date"
                                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        <div className="col-span-2 sm:col-span-1 flex items-end">
                            <button
                                onClick={() => setShowModalGaji(true)}
                                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 sm:px-4 py-2 text-sm rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center justify-center space-x-2"
                            >
                                <Download className="w-4 h-4" />
                                <span>Export</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <span>
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                                    </span>
                                                    {header.column.getIsSorted() && (
                                                        <span className="ml-2">
                                                            {header.column.getIsSorted() === 'desc' ? (
                                                                <ChevronDown className="w-4 h-4" />
                                                            ) : (
                                                                <ChevronUp className="w-4 h-4" />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {table.getRowModel().rows.map(row => (
                                    <tr key={row.id} className="hover:bg-gray-50 transition-colors duration-150">
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="bg-white px-4 sm:px-6 py-4 border-t border-gray-200">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-center space-x-2">
                                <span className="text-xs sm:text-sm text-gray-700">
                                    Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                                    {Math.min(
                                        (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                        table.getFilteredRowModel().rows.length
                                    )}{' '}
                                    of {table.getFilteredRowModel().rows.length} results
                                </span>
                            </div>

                            <div className="flex items-center space-x-1 sm:space-x-2">
                                <button
                                    onClick={() => table.setPageIndex(0)}
                                    disabled={!table.getCanPreviousPage()}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    First
                                </button>
                                <button
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                                <button
                                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                    disabled={!table.getCanNextPage()}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Last
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL GENERATE GAJI */}
            {
                showModalGaji && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-6 border-b border-slate-200">
                                <div>
                                    <h3 className="text-xl font-semibold text-slate-800">Generate Gaji Karyawan</h3>
                                    <p className="text-slate-500 text-sm mt-1">Masukkan komponen gaji</p>
                                </div>
                                <button
                                    onClick={() => setShowModalGaji(false)}
                                    className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                                >
                                    <X className="w-5 h-5 text-slate-600" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6 space-y-6">
                                {/* Basic Salary */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Gaji Pokok
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => adjustBasicSalary(-100000)}
                                            className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                                        >
                                            <Minus className="w-4 h-4 text-slate-600" />
                                        </button>
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                value={basicSalary}
                                                onChange={(e) => setBasicSalary(Number(e.target.value))}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center font-medium"
                                                placeholder="0"
                                            />
                                        </div>
                                        <button
                                            onClick={() => adjustBasicSalary(100000)}
                                            className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                                        >
                                            <Plus className="w-4 h-4 text-slate-600" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1 text-center">
                                        {formatCurrency(basicSalary)}
                                    </p>
                                </div>

                                {/* Overtime */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Lembur
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => adjustOvertime(-50000)}
                                            className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                                        >
                                            <Minus className="w-4 h-4 text-slate-600" />
                                        </button>
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                value={overtime}
                                                onChange={(e) => setOvertime(Number(e.target.value))}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center font-medium"
                                                placeholder="0"
                                            />
                                        </div>
                                        <button
                                            onClick={() => adjustOvertime(50000)}
                                            className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                                        >
                                            <Plus className="w-4 h-4 text-slate-600" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1 text-center">
                                        {formatCurrency(overtime)}
                                    </p>
                                </div>

                                {/* Income Components */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-medium text-slate-700">Pendapatan Tambahan</h4>
                                        <button
                                            onClick={addIncomeComponent}
                                            className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-lg hover:bg-green-200 transition-colors flex items-center space-x-1"
                                        >
                                            <Plus className="w-3 h-3" />
                                            <span>Tambah</span>
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        {incomeComponents.map((component) => (
                                            <div key={component.id} className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    placeholder="Nama komponen"
                                                    value={component.name}
                                                    onChange={(e) => updateIncomeComponent(component.id, 'name', e.target.value)}
                                                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                />
                                                <input
                                                    type="number"
                                                    placeholder="Jumlah"
                                                    value={component.amount}
                                                    onChange={(e) => updateIncomeComponent(component.id, 'amount', Number(e.target.value))}
                                                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-right"
                                                />
                                                <button
                                                    onClick={() => removeIncomeComponent(component.id)}
                                                    className="w-8 h-8 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Deduction Components */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-medium text-slate-700">Pengurangan</h4>
                                        <button
                                            onClick={addDeductionComponent}
                                            className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-lg hover:bg-red-200 transition-colors flex items-center space-x-1"
                                        >
                                            <Minus className="w-3 h-3" />
                                            <span>Tambah</span>
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        {deductionComponents.map((component) => (
                                            <div key={component.id} className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    placeholder="Nama komponen"
                                                    value={component.name}
                                                    onChange={(e) => updateDeductionComponent(component.id, 'name', e.target.value)}
                                                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                />
                                                <input
                                                    type="number"
                                                    placeholder="Jumlah"
                                                    value={component.amount}
                                                    onChange={(e) => updateDeductionComponent(component.id, 'amount', Number(e.target.value))}
                                                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-right"
                                                />
                                                <button
                                                    onClick={() => removeDeductionComponent(component.id)}
                                                    className="w-8 h-8 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-6 border-t border-slate-200 flex space-x-3">
                                <div className="flex-1 flex space-x-3">
                                    <button
                                        onClick={() => setShowModalGaji(false)}
                                        className="flex-1 px-4 py-3 border border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        onClick={handleGenerateGaji}
                                        className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                                    >
                                        <DollarSign className="w-4 h-4" />
                                        <span>Generate</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default AttendanceSystem;